import React from 'react';
import { X, FileText, Bookmark, Edit2 } from 'lucide-react';
import { useSavedIdeasStore } from '../../../../stores/savedIdeasStore';
import { useContentPlanStore } from '../../../../stores/contentPlanStore';
import type { Event } from '../../../../types/calendar';

interface ContentManagerModalProps {
  event: Event;
  onClose: () => void;
  onEdit: () => void;
}

export default function ContentManagerModal({ event, onClose, onEdit }: ContentManagerModalProps) {
  const { savedIdeas } = useSavedIdeasStore();
  const { plans } = useContentPlanStore();

  const attachedIdea = event.contentIdeaId ? 
    savedIdeas.find(idea => idea.title === event.contentIdeaId) : null;
  const attachedPlan = event.contentPlanId ? 
    plans.find(plan => plan.id === event.contentPlanId) : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-lg w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Conteúdo Vinculado</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {!attachedIdea && !attachedPlan ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Nenhum conteúdo vinculado
              </h3>
              <p className="text-gray-600 mb-4">
                Este evento não possui ideias ou planos vinculados.
              </p>
              <button
                onClick={onEdit}
                className="text-indigo-600 hover:text-indigo-700 font-medium"
              >
                Vincular conteúdo
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {attachedIdea && (
                <div className="bg-gray-50 p-6 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <Bookmark className="h-5 w-5 text-indigo-600" />
                      <h3 className="font-medium text-gray-900">Ideia Salva</h3>
                    </div>
                    <button
                      onClick={onEdit}
                      className="p-2 text-gray-600 hover:text-gray-900 rounded-full"
                      title="Editar"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                  </div>
                  <h4 className="font-medium text-gray-900 mb-2">{attachedIdea.title}</h4>
                  <p className="text-gray-600 mb-4">{attachedIdea.description}</p>
                  {attachedIdea.hashtags && (
                    <div className="flex flex-wrap gap-2">
                      {attachedIdea.hashtags.map((tag, index) => (
                        <span
                          key={index}
                          className="text-sm text-indigo-600 bg-indigo-50 px-2 py-1 rounded-full"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {attachedPlan && (
                <div className="bg-gray-50 p-6 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <FileText className="h-5 w-5 text-indigo-600" />
                      <h3 className="font-medium text-gray-900">Plano Vinculado</h3>
                    </div>
                    <button
                      onClick={onEdit}
                      className="p-2 text-gray-600 hover:text-gray-900 rounded-full"
                      title="Editar"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                  </div>
                  <h4 className="font-medium text-gray-900 mb-2">{attachedPlan.title}</h4>
                  <p className="text-sm text-gray-500">
                    Criado em {new Date(attachedPlan.createdAt).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4 bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}